<template>
  <div>
    <a-alert
      type="info"
      show-icon
      class="mb-3"
      :message="$t('comptabilite.documentDisplayIntro')"
    />

    <a-tabs v-model="docTab" type="card">
      <a-tab-pane key="receipt" :tab="$t('invoice.recu')" />
      <a-tab-pane key="invoice" :tab="$t('invoice.facture')" />
    </a-tabs>

    <div v-if="!activeBuilding" class="text-muted">
      {{ $t("warning.connectionProblem") }}
    </div>

    <a-form v-else layout="vertical" class="document-display-form">
      <div class="mb-3 d-flex flex-wrap" style="gap: 8px">
        <a-button type="primary" :loading="saving" @click="save">
          {{ $t("action.enregistrer") }}
        </a-button>
        <a-button
          type="default"
          icon="eye"
          :loading="previewLoading"
          @click="preview"
        >
          {{ $t("comptabilite.previewDocument") }}
        </a-button>
      </div>

      <div class="row">
        <div class="col-md-6">
          <a-divider orientation="left">{{
            $t("comptabilite.documentDisplayHeader")
          }}</a-divider>

          <a-form-item :label="$t('comptabilite.showLogo')">
            <a-switch v-model="form.showLogo" />
          </a-form-item>

          <a-form-item :label="$t('comptabilite.titleMode')">
            <a-radio-group v-model="form.titleMode">
              <a-radio value="default">{{
                $t("comptabilite.titleModeDefault")
              }}</a-radio>
              <a-radio value="custom">{{
                $t("comptabilite.titleModeCustom")
              }}</a-radio>
            </a-radio-group>
          </a-form-item>

          <a-form-item
            v-if="form.titleMode === 'custom'"
            :label="$t('comptabilite.customTitle')"
          >
            <a-input v-model="form.customTitle" :maxLength="120" />
          </a-form-item>

          <a-form-item :label="$t('comptabilite.includeSchoolTypeInTitle')">
            <a-switch
              v-model="form.includeSchoolTypeInTitle"
              :disabled="form.titleMode === 'custom'"
            />
          </a-form-item>

          <a-form-item :label="$t('comptabilite.showClass')">
            <a-switch v-model="form.showClass" />
          </a-form-item>

          <a-divider orientation="left">{{
            $t("comptabilite.documentDisplayFooter")
          }}</a-divider>

          <a-form-item :label="$t('comptabilite.showFooterText')">
            <a-switch v-model="form.showFooterText" />
          </a-form-item>
          <a-form-item
            v-if="form.showFooterText"
            :label="$t('comptabilite.footerText')"
          >
            <a-textarea v-model="form.footerText" :rows="3" :maxLength="400" />
          </a-form-item>

          <a-form-item :label="$t('comptabilite.showCachetSignatureLabel')">
            <a-switch v-model="form.showCachetSignatureLabel" />
          </a-form-item>
          <a-form-item :label="$t('comptabilite.showStamp')">
            <a-switch v-model="form.showStamp" />
          </a-form-item>
          <a-form-item
            v-if="form.showStamp"
            :label="$t('comptabilite.stampScale')"
          >
            <div class="d-flex align-items-center" style="gap: 12px">
              <a-slider
                :value="form.stampScale"
                :min="0.5"
                :max="2.5"
                :step="0.1"
                style="flex: 1; margin: 0"
                @change="onStampScaleChange"
              />
              <span style="min-width: 40px">{{ form.stampScale }}×</span>
            </div>
            <div class="text-muted" style="font-size: 12px">
              {{ $t("comptabilite.stampScaleHint") }}
            </div>
          </a-form-item>
          <a-form-item :label="$t('comptabilite.showSignature')">
            <a-switch v-model="form.showSignature" />
          </a-form-item>

          <a-divider orientation="left">{{
            $t("comptabilite.documentDisplayPayment")
          }}</a-divider>

          <a-form-item :label="$t('comptabilite.showTuitionSummary')">
            <a-switch v-model="form.showTuitionSummary" />
          </a-form-item>
          <a-form-item :label="$t('comptabilite.showActivitySummary')">
            <a-switch v-model="form.showActivitySummary" />
            <div class="text-muted" style="font-size: 12px">
              {{ $t("comptabilite.paymentSummaryHint") }}
            </div>
          </a-form-item>
          <a-form-item :label="$t('comptabilite.showTableNote')">
            <a-switch v-model="form.showTableNote" />
            <div class="text-muted" style="font-size: 12px">
              {{ $t("comptabilite.tableNoteHint") }}
            </div>
          </a-form-item>
          <template v-if="form.showTableNote">
            <a-form-item :label="$t('comptabilite.tableNoteText')">
              <a-textarea
                v-model="form.tableNoteText"
                :rows="4"
                :maxLength="1000"
                :dir="tableNoteDir"
              />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.tableNoteAlign')">
              <a-radio-group v-model="form.tableNoteAlign" button-style="solid">
                <a-radio-button value="auto">{{
                  $t("comptabilite.tableNoteAlignAuto")
                }}</a-radio-button>
                <a-radio-button value="left">{{
                  $t("comptabilite.tableNoteAlignLeft")
                }}</a-radio-button>
                <a-radio-button value="center">{{
                  $t("comptabilite.tableNoteAlignCenter")
                }}</a-radio-button>
                <a-radio-button value="right">{{
                  $t("comptabilite.tableNoteAlignRight")
                }}</a-radio-button>
              </a-radio-group>
            </a-form-item>
            <div class="d-flex flex-wrap" style="gap: 16px">
              <a-form-item :label="$t('comptabilite.tableNoteColor')">
                <a-input
                  type="color"
                  v-model="form.tableNoteColor"
                  style="width: 80px; padding: 2px"
                />
              </a-form-item>
              <a-form-item :label="$t('comptabilite.tableNoteFontSize')">
                <a-input-number
                  v-model="form.tableNoteFontSize"
                  :min="6"
                  :max="24"
                  :step="1"
                />
              </a-form-item>
            </div>
          </template>
          <a-form-item :label="$t('comptabilite.showPaymentMethodDetails')">
            <a-switch v-model="form.showPaymentMethodDetails" />
          </a-form-item>
          <a-form-item :label="$t('comptabilite.showOnTransReduction')">
            <a-switch v-model="form.showOnTransReduction" />
          </a-form-item>
          <a-form-item :label="$t('comptabilite.baseReductionDisplay')">
            <a-select v-model="form.baseReductionDisplay" style="width: 100%">
              <a-select-option value="hidden">{{
                $t("comptabilite.baseReductionHidden")
              }}</a-select-option>
              <a-select-option value="total">{{
                $t("comptabilite.baseReductionTotal")
              }}</a-select-option>
              <a-select-option value="percentPerLine">{{
                $t("comptabilite.baseReductionPercentPerLine")
              }}</a-select-option>
            </a-select>
          </a-form-item>

          <a-divider orientation="left">{{
            $t("comptabilite.documentDisplayPrint")
          }}</a-divider>

          <a-form-item :label="$t('comptabilite.copiesPerPage')">
            <a-radio-group v-model="form.copiesPerPage">
              <a-radio :value="1">{{
                $t("comptabilite.copiesPerPage1")
              }}</a-radio>
              <a-radio :value="2">{{
                $t("comptabilite.copiesPerPage2")
              }}</a-radio>
            </a-radio-group>
          </a-form-item>

          <a-form-item
            v-if="docTab === 'receipt'"
            :label="$t('comptabilite.autoPrint')"
          >
            <a-switch v-model="form.autoPrint" />
          </a-form-item>

          <a-divider orientation="left">{{
            $t("comptabilite.documentNumbering")
          }}</a-divider>

          <a-alert
            type="warning"
            show-icon
            class="mb-3"
            :message="
              docTab === 'invoice'
                ? $t('comptabilite.numberFormatTabInvoice')
                : $t('comptabilite.numberFormatTabReceipt')
            "
          />

          <template v-if="form.numberFormat">
            <a-form-item :label="$t('comptabilite.numberFormatCustom')">
              <a-switch v-model="form.numberFormat.custom" />
              <div class="text-muted" style="font-size: 12px">
                {{ $t("comptabilite.numberFormatCustomHint") }}
              </div>
            </a-form-item>

            <template v-if="form.numberFormat.custom">
              <a-form-item :label="$t('comptabilite.numberFormatPrefix')">
                <a-input
                  v-model="form.numberFormat.prefix"
                  :maxLength="32"
                  :placeholder="docTab === 'invoice' ? 'FACT' : 'REC'"
                />
              </a-form-item>

              <a-form-item :label="$t('comptabilite.numberFormatDatePattern')">
                <a-select
                  v-model="form.numberFormat.datePattern"
                  style="width: 100%"
                  :disabled="hasFreePattern"
                >
                  <a-select-option value="none">{{
                    $t("comptabilite.numberFormatDateNone")
                  }}</a-select-option>
                  <a-select-option value="YYYYMM"
                    >YYYYMM (202609)</a-select-option
                  >
                  <a-select-option value="YYYY">YYYY (2026)</a-select-option>
                  <a-select-option value="YY">YY (26)</a-select-option>
                  <a-select-option value="YYYY-MM">YYYY-MM</a-select-option>
                  <a-select-option value="DD.MM.YYYY"
                    >DD.MM.YYYY</a-select-option
                  >
                </a-select>
              </a-form-item>

              <a-form-item :label="$t('comptabilite.numberFormatSeparator')">
                <a-input
                  v-model="form.numberFormat.separator"
                  :maxLength="3"
                  :disabled="hasFreePattern"
                  placeholder="-"
                />
                <div class="text-muted" style="font-size: 12px">
                  {{ $t("comptabilite.numberFormatSeparatorHint") }}
                </div>
              </a-form-item>

              <a-form-item :label="$t('comptabilite.numberFormatPadding')">
                <a-input-number
                  v-model="form.numberFormat.sequencePadding"
                  :min="3"
                  :max="8"
                  :step="1"
                  style="width: 100%"
                />
              </a-form-item>

              <a-form-item :label="$t('comptabilite.numberFormatPattern')">
                <a-input
                  v-model="form.numberFormat.pattern"
                  :maxLength="80"
                  :placeholder="$t('comptabilite.numberFormatPatternPlaceholder')"
                  allow-clear
                />
                <div class="mt-2" style="display: flex; flex-wrap: wrap; gap: 8px">
                  <a-button size="small" @click="applyPatternPreset('parts')">
                    {{ $t("comptabilite.numberFormatPresetParts") }}
                  </a-button>
                  <a-button
                    size="small"
                    @click="applyPatternPreset('prefix-yyyy-seq')"
                  >
                    {{ $t("comptabilite.numberFormatPresetYyyy") }}
                  </a-button>
                  <a-button
                    size="small"
                    @click="applyPatternPreset('prefix-yyyymm-seq')"
                  >
                    {{ $t("comptabilite.numberFormatPresetYyyymm") }}
                  </a-button>
                </div>
                <div class="text-muted" style="font-size: 12px; margin-top: 6px">
                  {{ $t("comptabilite.numberFormatPatternHint") }}
                </div>
                <a-alert
                  v-if="patternMissingSeq"
                  type="warning"
                  show-icon
                  class="mt-2"
                  :message="$t('comptabilite.numberFormatSeqRequired')"
                />
                <a-alert
                  v-if="patternHasUnsafeChars"
                  type="warning"
                  show-icon
                  class="mt-2"
                  :message="$t('comptabilite.numberFormatUnsafeChars')"
                />
              </a-form-item>
            </template>

            <a-form-item :label="$t('comptabilite.numberFormatStartNumber')">
              <a-input-number
                v-model="form.numberFormat.startNumber"
                :min="1"
                :max="99999999"
                :step="1"
                :precision="0"
                style="width: 100%"
              />
              <div class="text-muted" style="font-size: 12px">
                {{ $t("comptabilite.numberFormatStartNumberHint") }}
              </div>
            </a-form-item>

            <a-alert
              type="info"
              show-icon
              class="mb-3"
              :message="
                $t('comptabilite.numberFormatExample', {
                  example: serialExample,
                })
              "
            />
          </template>

          <template v-if="docTab === 'invoice' && form.fiscal">
            <a-divider orientation="left">{{
              $t("comptabilite.documentDisplayFiscal")
            }}</a-divider>

            <a-form-item :label="$t('comptabilite.tvaApplicable')">
              <a-switch v-model="form.fiscal.tvaApplicable" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.showTva')">
              <a-switch v-model="form.fiscal.showTva" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.tvaRate')">
              <a-input-number
                v-model="form.fiscal.tvaRate"
                :min="0"
                :max="1"
                :step="0.01"
                :precision="4"
                style="width: 100%"
                :placeholder="envTvaPlaceholder"
              />
              <div class="text-muted" style="font-size: 12px">
                {{ $t("comptabilite.tvaRateHint") }}
              </div>
            </a-form-item>

            <a-form-item :label="$t('comptabilite.timbreApplicable')">
              <a-switch v-model="form.fiscal.timbreApplicable" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.showTimbre')">
              <a-switch v-model="form.fiscal.showTimbre" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.timbreAmount')">
              <a-input-number
                v-model="form.fiscal.timbreAmount"
                :min="0"
                :step="0.001"
                :precision="3"
                style="width: 100%"
                :placeholder="envFiscalPlaceholder"
              />
              <div class="text-muted" style="font-size: 12px">
                {{ $t("comptabilite.timbreAmountHint") }}
              </div>
            </a-form-item>

            <a-form-item :label="$t('comptabilite.retenueApplicable')">
              <a-switch v-model="form.fiscal.retenueApplicable" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.showRetenue')">
              <a-switch v-model="form.fiscal.showRetenue" />
            </a-form-item>
            <a-form-item :label="$t('comptabilite.retenuePercent')">
              <a-input-number
                v-model="form.fiscal.retenuePercent"
                :min="0"
                :max="100"
                :step="0.1"
                :precision="2"
                style="width: 100%"
                :disabled="!form.fiscal.retenueApplicable"
              />
              <div class="text-muted" style="font-size: 12px">
                {{ $t("comptabilite.retenuePercentHint") }}
              </div>
            </a-form-item>
          </template>
        </div>

        <div class="col-md-6">
          <a-divider orientation="left">{{
            $t("comptabilite.documentDisplayPreview")
          }}</a-divider>
          <iframe
            v-if="previewUrl"
            class="preview-frame"
            :src="previewUrl"
            title="preview"
          />
          <div v-else class="preview-placeholder">
            {{ $t("comptabilite.previewHint") }}
          </div>
        </div>
      </div>
    </a-form>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState } from "vuex";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import { printInvoice } from "@/utilities/printInvoice";
import {
  DOCUMENT_TEMPLATE_DEFAULTS,
  INVOICE_FISCAL_DEFAULTS,
  NUMBER_FORMAT_DEFAULTS,
  resolveDocumentTemplate,
  resolveInvoiceFiscalConfig,
  resolveNumberFormat,
  previewDocumentSerial,
  buildPreviewRecord,
  clampStampScale,
  clampTableNoteFontSize,
  normalizeHexColor,
} from "@/utilities/documentTemplateConfig";

export default {
  name: "DocumentDisplayTab",
  data() {
    return {
      docTab: "receipt",
      form: {
        ...DOCUMENT_TEMPLATE_DEFAULTS,
        fiscal: { ...INVOICE_FISCAL_DEFAULTS },
        numberFormat: { ...NUMBER_FORMAT_DEFAULTS },
      },
      saving: false,
      previewLoading: false,
      previewUrl: null,
    };
  },
  computed: {
    ...mapState(["user", "settings"]),
    activeBuilding() {
      return (this.user.building || []).find(
        (b) => b.dbName === this.settings.activeBuilding
      );
    },
    envTvaPlaceholder() {
      const n = Number(process.env.VUE_APP_TVA);
      return Number.isFinite(n) ? String(n) : "0.07";
    },
    envFiscalPlaceholder() {
      const n = Number(process.env.VUE_APP_FISCAL);
      return Number.isFinite(n) ? String(n) : "1";
    },
    serialExample() {
      const cfg = resolveNumberFormat(this.form.numberFormat || NUMBER_FORMAT_DEFAULTS);
      return [0, 1, 2]
        .map((i) => previewDocumentSerial(cfg, new Date(), cfg.startNumber + i))
        .join(", ");
    },
    hasFreePattern() {
      const p =
        this.form.numberFormat && this.form.numberFormat.pattern
          ? String(this.form.numberFormat.pattern).trim()
          : "";
      return p.length > 0;
    },
    patternMissingSeq() {
      const p =
        this.form.numberFormat && this.form.numberFormat.pattern
          ? String(this.form.numberFormat.pattern).trim()
          : "";
      return p.length > 0 && !/\{SEQ\}/i.test(p);
    },
    tableNoteDir() {
      return /[\u0600-\u06FF]/.test(this.form.tableNoteText || "") ? "rtl" : "auto";
    },
    patternHasUnsafeChars() {
      const p =
        this.form.numberFormat && this.form.numberFormat.pattern
          ? String(this.form.numberFormat.pattern)
          : "";
      return /[/\\:*?"<>|]/.test(p);
    },
  },
  watch: {
    docTab() {
      this.loadForm();
      this.revokePreview();
    },
    "settings.activeBuilding"() {
      this.loadForm();
      this.revokePreview();
    },
  },
  mounted() {
    this.loadForm();
  },
  beforeDestroy() {
    this.revokePreview();
  },
  methods: {
    applyPatternPreset(kind) {
      if (!this.form.numberFormat) {
        this.$set(this.form, "numberFormat", { ...NUMBER_FORMAT_DEFAULTS });
      }
      this.form.numberFormat.custom = true;
      const prefix =
        (this.form.numberFormat.prefix &&
          String(this.form.numberFormat.prefix).trim()) ||
        (this.docTab === "invoice" ? "FACT" : "REC");
      this.form.numberFormat.prefix = prefix;
      this.form.numberFormat.sequencePadding =
        this.form.numberFormat.sequencePadding || 3;

      if (kind === "parts") {
        // Clear free pattern → use prefix + date + separator fields
        this.form.numberFormat.pattern = "";
        if (!this.form.numberFormat.datePattern) {
          this.form.numberFormat.datePattern = "YYYY";
        }
        if (
          this.form.numberFormat.separator === undefined ||
          this.form.numberFormat.separator === null
        ) {
          this.form.numberFormat.separator = "-";
        }
        return;
      }
      if (kind === "prefix-yyyy-seq") {
        this.form.numberFormat.pattern = "{PREFIX}-{YYYY}-{SEQ}";
        this.form.numberFormat.datePattern = "YYYY";
        this.form.numberFormat.separator = "-";
        return;
      }
      if (kind === "prefix-yyyymm-seq") {
        this.form.numberFormat.pattern = "{PREFIX}-{YYYYMM}-{SEQ}";
        this.form.numberFormat.datePattern = "YYYYMM";
        this.form.numberFormat.separator = "-";
      }
    },
    loadForm() {
      if (!this.activeBuilding) {
        this.form = {
          ...DOCUMENT_TEMPLATE_DEFAULTS,
          fiscal: { ...INVOICE_FISCAL_DEFAULTS },
          numberFormat: { ...NUMBER_FORMAT_DEFAULTS },
        };
        return;
      }
      const resolved = resolveDocumentTemplate(
        this.activeBuilding,
        this.docTab
      );
      this.form = {
        ...resolved,
        fiscal: resolveInvoiceFiscalConfig(resolved.fiscal),
        numberFormat: resolveNumberFormat(resolved.numberFormat),
      };
    },
    onStampScaleChange(value) {
      this.form.stampScale = clampStampScale(value);
    },
    revokePreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
    async preview() {
      if (!this.activeBuilding) return;
      this.previewLoading = true;
      try {
        this.revokePreview();
        const typeFR = this.docTab === "invoice" ? "F" : "R";
        const fiscal =
          this.docTab === "invoice"
            ? resolveInvoiceFiscalConfig(this.form.fiscal)
            : null;
        const numberFormat = resolveNumberFormat(this.form.numberFormat);
        const blob = await printInvoice(
          buildPreviewRecord(typeFR, fiscal, numberFormat),
          this.settings,
          this.user,
          {
            templateOverride: {
              ...this.form,
              stampScale: clampStampScale(this.form.stampScale),
              tableNoteColor: normalizeHexColor(this.form.tableNoteColor),
              tableNoteFontSize: clampTableNoteFontSize(this.form.tableNoteFontSize),
              fiscal:
                this.docTab === "invoice"
                  ? resolveInvoiceFiscalConfig(this.form.fiscal)
                  : undefined,
              numberFormat,
            },
            output: "blob",
          }
        );
        this.previewUrl = URL.createObjectURL(blob);
      } catch (err) {
        console.error("Document preview failed:", err);
        showApiError(this, err);
      } finally {
        this.previewLoading = false;
      }
    },
    async save() {
      if (!this.activeBuilding) return;
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          stampScale: clampStampScale(this.form.stampScale),
          tableNoteColor: normalizeHexColor(this.form.tableNoteColor),
          tableNoteFontSize: clampTableNoteFontSize(this.form.tableNoteFontSize),
          numberFormat: resolveNumberFormat(this.form.numberFormat),
        };
        delete payload.showPaymentSummary;
        if (this.docTab === "invoice") {
          payload.fiscal = resolveInvoiceFiscalConfig(this.form.fiscal);
        } else {
          delete payload.fiscal;
        }
        // Only send the active tab so the other tab's numbering/fiscal is never
        // overwritten by a stale vuex copy.
        const config = {
          documentTemplates: {
            [this.docTab]: payload,
          },
        };
        const res = await apiClient.post(
          `/buildings/${this.activeBuilding._id}/updateConfig`,
          { config }
        );

        const savedConfig =
          (res && res.data && res.data.config) || {
            ...(this.activeBuilding.config || {}),
            documentTemplates: {
              ...((this.activeBuilding.config &&
                this.activeBuilding.config.documentTemplates) ||
                {}),
              [this.docTab]: payload,
            },
          };

        const buildings = (this.user.building || []).map((b) =>
          String(b._id) === String(this.activeBuilding._id)
            ? { ...b, config: savedConfig }
            : b
        );
        this.$store.commit("user/SET_BUILDING", buildings);
        this.$message.success(this.$t("success.changementEffectue"));
        // Refresh form + preview from what the server actually stored
        this.loadForm();
        await this.preview();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.preview-frame {
  width: 100%;
  height: 640px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fafafa;
}
.preview-placeholder {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  color: #8c8c8c;
  padding: 16px;
  text-align: center;
}
.document-display-form .ant-form-item {
  margin-bottom: 12px;
}
</style>
